import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAgentCapabilities } from '@/features/ai-agent/hooks/useAgentCapabilities';
import { useContextDetection } from '@/hooks/useContextDetection';
import { useSystemStateQuery } from './useSystemStateQuery';
import { quickActionsService } from '@flowstate/quick-actions';
import type { QuickAction, QuickActionGroup } from '@/features/ai-agent/types/quickActions';
import { logger } from "@/lib/logger";

interface UseQuickActionsQueryOptions {
  /**
   * Enable automatic refetching when dependencies change
   * @default true
   */
  enabled?: boolean;
  /**
   * How long generated actions are considered fresh (in milliseconds)
   * @default 10000 (10 seconds)
   */
  staleTime?: number;
}

interface QuickActionsQueryResult {
  actions: QuickAction[];
  groupedActions: QuickActionGroup[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * TanStack Query hook for Quick Actions
 * 
 * Generates context-aware actions based on:
 * - Agent capabilities (role, permissions)
 * - Current context (route, page)
 * - System state (overdue tasks, events, etc.)
 * 
 * @example
 * ```tsx
 * const { actions, groupedActions, isLoading } = useQuickActionsQuery();
 * 
 * return (
 *   <div>
 *     {isLoading ? 'Loading...' : actions.map(action => (
 *       <ActionButton key={action.id} action={action} />
 *     ))}
 *   </div>
 * );
 * ```
 */
export function useQuickActionsQuery(
  options: UseQuickActionsQueryOptions = {}
): QuickActionsQueryResult {
  const {
    enabled = true,
    staleTime = 10000, // 10 seconds
  } = options;

  // Get context from custom hooks (no network calls)
  const agentContext = useAgentCapabilities();
  const { currentContext } = useContextDetection();

  // Get system state from TanStack Query
  const {
    data: systemState,
    isLoading: isSystemStateLoading,
  } = useSystemStateQuery();

  // Generate query key with relevant dependencies
  // Only include values that should trigger re-generation
  const queryKey = useMemo(() => {
    return [
      'quickActions',
      agentContext?.role,
      agentContext?.expertise?.domain,
      currentContext?.route,
      systemState?.overdueTasks,
      systemState?.tasksDueToday,
      systemState?.upcomingEvents,
      systemState?.calendarSyncStatus,
      systemState?.dealsAtRisk,
      systemState?.streakAtRisk,
      systemState?.currentStreak,
    ];
  }, [
    agentContext?.role,
    agentContext?.expertise?.domain,
    currentContext?.route,
    systemState?.overdueTasks,
    systemState?.tasksDueToday,
    systemState?.upcomingEvents,
    systemState?.calendarSyncStatus,
    systemState?.dealsAtRisk,
    systemState?.streakAtRisk,
    systemState?.currentStreak,
  ]);

  // Query for generating actions
  const query = useQuery<QuickAction[], Error>({
    queryKey,

    // Query function - generates actions based on context
    queryFn: () => {
      logger.info('[QuickActionsQuery] Query key:', { queryKey });
      logger.info('[QuickActionsQuery] Generating actions with context:', {
        hasAgentContext: !!agentContext,
        role: agentContext?.role,
        hasRole: agentContext?.role !== undefined,
        domain: agentContext?.expertise?.domain,
        route: currentContext?.route,
        systemState: systemState ? 'loaded' : 'null',
        overdueTasks: systemState?.overdueTasks,
      });

      try {
        const actions = quickActionsService.generateActions(
          agentContext,
          currentContext,
          systemState ?? undefined
        );

        logger.info('[QuickActionsQuery] Generated', { count: actions.length, type: 'actions' });
        logger.info('[QuickActionsQuery] Actions', {
          actions: actions.map(a => ({ id: a.id, label: a.label, icon: typeof a.icon }))
        });
        return actions;
      } catch (error) {
        logger.error('[QuickActionsQuery] Error generating actions:', { error: error });
        return [];
      }
    },

    // Run as soon as agentContext exists (role can be undefined initially, will update when loaded)
    enabled: enabled && !!agentContext,

    // Cache configuration
    staleTime, // Actions can be stale for 10s
    gcTime: 5 * 60 * 1000, // Keep for 5 minutes

    // Don't refetch automatically - actions should update when dependencies change
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    // Process and filter actions using select for memoization
    select: (rawActions) => {
      logger.info('[QuickActionsQuery] select() received', { rawActionCount: rawActions.length });
      
      // React components (Lucide icons) are objects, not functions
      // Just ensure the icon exists and is truthy
      const filtered = rawActions.filter(
        (action) => action && action.icon
      );
      logger.info('[QuickActionsQuery] select() after icon filter', { filteredCount: filtered.length });
      return filtered;
    },

    // Retry on failure
    retry: 1,
    retryDelay: 1000,

    // Placeholder while loading
    placeholderData: [],
  });

  // Group actions by category (memoized)
  const groupedActions = useMemo(() => {
    if (!query.data) return [];
    return quickActionsService.groupActionsByCategory(query.data);
  }, [query.data]);

  // Return unified interface
  return {
    actions: query.data ?? [],
    groupedActions,
    isLoading: query.isLoading || isSystemStateLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook to get just the actions array (no loading/error states)
 * Useful when you only need the actions and have a parent handling loading states
 */
export function useQuickActionsData(): QuickAction[] {
  const { actions } = useQuickActionsQuery();
  return actions;
}
